import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { scaleBand, scaleLinear, max, axisBottom, axisLeft, axisRight, select, format, pointer } from "d3";

export default function ElectricityGraph({ className, data }: { className?: string; data: any[] }) {
  const svgRef = useRef(null);
  const [width, setWidth] = useState(700);
  const [height, setHeight] = useState(500);
  const [tooltipValue1, setTooltipValue1] = useState("");
  const [tooltipValue2, setTooltipValue2] = useState("");
  const [tooltipValue3, setTooltipValue3] = useState("");

  const margin = useMemo(() => ({ left: 1, right: 50, top: 10, bottom: 10 }), []);

  const minorTickSize = 10;
  const allowance = 54;
  const bottom = height - margin.bottom - margin.top - minorTickSize - 14;

  const xScale = useMemo(
    () =>
      scaleBand()
        .domain(
          data.map((d: any) =>
            new Date(d.timestamp).toLocaleString("en-GB", {
              day: "2-digit",
            })
          )
        )
        .padding(0.5)
        .range([minorTickSize + margin.left + allowance, width - margin.right]),
    [data, width, margin]
  );

  const yScale1 = useMemo(() => {
    const maxValue = max(data ? data : [], (d: any) => parseFloat(d.chp_production + d.imported_from_grid));

    return scaleLinear()
      .domain([0, maxValue + maxValue / 2])
      .nice()
      .range([bottom / 2, 0]);
  }, [data, bottom]);
  const yScale2 = useMemo(() => {
    const maxValue = max(data ? data : [], (d: any) => parseFloat(d.sold_to_grid));

    return scaleLinear()
      .domain([maxValue + maxValue / 1.2, 0])
      .nice()
      .range([bottom, bottom / 2]);
  }, [data, bottom]);

  const drawAxis = useCallback(({ container, xScale, yScale, right, tickArguments, tickSize, tickFormat, color, transform, noText }: any) => {
    const scale = xScale || yScale;
    const axisType = xScale ? axisBottom : !right ? axisLeft : axisRight;
    const axis = axisType(scale).tickArguments(tickArguments).tickSize(tickSize).tickFormat(tickFormat);
    const axisGroup = container.append("g").attr("class", "axis").attr("color", color).attr("transform", transform).call(axis);
    axisGroup
      .selectAll("line")
      .attr("stroke", color)
      .attr("stroke-width", "0.5");
    axisGroup
      .selectAll("text")
      .attr("color", "#95A0B7")
      // .attr("transform", "rotate(-45)")
      // .attr("transform-origin", "0 20")
      .attr("font-size", "0.75rem");
    if (noText) {
      axisGroup.selectAll("text").remove();
    }
  }, []);

  useEffect(() => {
    // Create root container where we will append all other chart elements
    const svgEl = select(svgRef.current).select(".axis-group");
    svgEl.selectAll(".axis").remove();
    // Add X grid lines with labels
    drawAxis({
      xScale,
      container: svgEl,
      color: "rgb(149 160 183)",
      tickSize: 0,
      tickArguments: [1, "%b %d"],
      transform: `translate(0, ${height - margin.bottom + margin.top - minorTickSize - 14})`,
    });

    // Add Y grid lines with labels
    drawAxis({
      yScale: yScale1,
      container: svgEl,
      color: "rgb(232 233 237)",
      tickSize: margin.right - width + allowance + minorTickSize,
      tickArguments: [4, "~s"],
      transform: `translate(${margin.left + minorTickSize + allowance}, ${margin.top})`,
      tickFormat: (val: any) => `${format("~s")(val)}W`,
    });
    // Add Y grid lines with labels to the right
    drawAxis({
      yScale: yScale2,
      container: svgEl,
      right: true,
      color: (val: any) => `${val === 0 ? "#95A0B7" : "rgb(232 233 237)"}`,
      tickSize: margin.right - width + allowance + minorTickSize,
      tickArguments: [4, "~s"],
      transform: `translate(${width - margin.right}, ${margin.top})`,
      tickFormat: (val: any) => `${format("~s")(val)}W`,
    });
    svgEl.selectAll(".domain").remove();
  }, [drawAxis, width, height, margin, xScale, yScale1, yScale2]);

  const resizeWindow = () => {
    const rect = svgRef.current.getBoundingClientRect();
    setWidth(rect.width);
    setHeight(rect.height);
  };

  useEffect(() => {
    resizeWindow();
    window.addEventListener("resize", resizeWindow);
    return () => window.removeEventListener("resize", resizeWindow);
  }, []);

  const drawBackground = () => {
    // reset background size to defaults
    const contentBackground = select(svgRef.current).select(".contentBackground");
    contentBackground.attr("width", 120).attr("height", 40);

    // calculate new background size
    const tooltipContentElement = select(svgRef.current).select(".content").node() as SVGElement;
    if (!tooltipContentElement) return;

    const contentSize = tooltipContentElement.getBoundingClientRect();

    contentBackground.attr("width", contentSize.width + 8).attr("height", contentSize.height + 4);

    return contentSize;
  };

  const handleMouseOver = () => {
    select(svgRef.current).select(".tooltip").transition().duration(10).attr("display", "block");
  };

  const handleMouseMove = (e: any, index: number) => {
    const [x, y] = pointer(e);
    const tooltip = select(svgRef.current).select(".tooltip");
    setTooltipValue1(`${format("~s")(data[index].chp_production)}W`);
    setTooltipValue2(`${format("~s")(data[index].imported_from_grid)}W`);
    setTooltipValue3(`${format("~s")(data[index].sold_to_grid)}W`);
    // tooltip.select("circle").attr("fill", color);
    tooltip.transition().duration(10).attr("display", "block");
    const contentSize = drawBackground();

    const translateX = contentSize.width + x + 20 > width ? x - 20 - contentSize.width : x + 20;
    const translateY = contentSize.height + y + 10 > height ? y + 10 - contentSize.height : y + 10;

    tooltip.attr("transform", `translate(${translateX}, ${translateY})`);
  };

  const handleMouseOut = (e: any) => {
    select(svgRef.current).select(".tooltip").transition().duration(10).attr("display", "none");
  };

  return (
    <div className="flex flex-col justify-between">
      <svg overflow="visible" ref={(node) => (node ? (svgRef.current = node) : null)} className="h-48 md:h-80 lg:h-auto py-2 cursor-crosshair" viewBox={`0 0 ${width} ${height}`}>
        <g className="axis-group" />
        <g className="chart">
          <text fill="#5f698f" className="italic" transform={`translate(${margin.left + minorTickSize + allowance + 14}, ${margin.top + 32})`}>
            Consumed
          </text>
          <text fill="#5f698f" className="italic" transform={`translate(${width - margin.right - 90}, ${bottom - 24})`}>
            Sold to grid
          </text>
          <g>
            {data.map((item: any, index: number) => (
              <path
                key={item.timestamp}
                fill="#97ABF1"
                d={`M${
                  xScale(
                    new Date(item.timestamp).toLocaleString("en-GB", {
                      // month: "short",
                      day: "2-digit",
                    })
                  ) + xScale.bandwidth()
                },${yScale1(item.imported_from_grid) + margin.top}  v${-(
                  yScale1(item.imported_from_grid) -
                  yScale1(item.chp_production + item.imported_from_grid) -
                  5
                )} q0,-5 -5,-5 h${-(xScale.bandwidth() - 10)} q-5,0 -5,5 v${
                  yScale1(item.imported_from_grid) - yScale1(item.chp_production + item.imported_from_grid) - 5
                } z`}
                onMouseOver={handleMouseOver}
                onMouseMove={(e: any) => handleMouseMove(e, index)}
                onMouseOut={handleMouseOut}
              />
            ))}
          </g>
          <g>
            {data.map((item: any, index: number) => (
              <rect
                key={item.timestamp}
                fill="#5273E8"
                x={xScale(
                  new Date(item.timestamp).toLocaleString("en-GB", {
                    // month: "short",
                    day: "2-digit",
                  })
                )}
                y={yScale1(item.imported_from_grid) + margin.top}
                width={xScale.bandwidth()}
                height={bottom / 2 - yScale1(item.imported_from_grid)}
                onMouseOver={handleMouseOver}
                onMouseMove={(e: any) => handleMouseMove(e, index)}
                onMouseOut={handleMouseOut}
              />
            ))}
          </g>
          <g>
            {data.map((item: any, index: number) => (
              <path
                key={item.timestamp}
                fill="#2AC0D5"
                d={`M${
                  xScale(
                    new Date(item.timestamp).toLocaleString("en-GB", {
                      day: "2-digit",
                    })
                  ) + xScale.bandwidth()
                },${yScale2(0) + margin.top}  v${yScale2(item.sold_to_grid) - yScale2(0) - 5} q0,5 -5,5 h${-(
                  xScale.bandwidth() - 10
                )} q-5,0 -5,-5 v${-(yScale2(item.sold_to_grid) - yScale2(0) - 5)} z`}
                onMouseOver={handleMouseOver}
                onMouseMove={(e: any) => handleMouseMove(e, index)}
                onMouseOut={handleMouseOut}
              />
            ))}
          </g>
        </g>
        <g className="tooltip" display="none">
          <rect className="contentBackground" rx={1} ry={1} fill="#fff" strokeWidth={1} stroke="#f0f0f0" filter="drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.05))" />

          <g className="content">
            {/* <text className="contentTitle" transform="translate(4,14)" /> */}
            <g transform={`translate(1,10)`}>
              {/* <circle r={6} fill="#97ABF1" /> */}
              <text className="item-value" transform="translate(4,4)" fill="#6a86e7" fontSize="11">
                Produced By CHP: {tooltipValue1}
              </text>
            </g>
            <g transform={`translate(1,26)`}>
              {/* <circle r={6} fill="#5273E8" /> */}
              <text className="item-value" transform="translate(4,4)" fill="#1d4ed8" fontSize="11">
                <tspan x="0" dy="0.5em">Imported from Grid: {tooltipValue2}</tspan>
              </text>
            </g>
            <g transform={`translate(1,42)`}>
              {/* <circle r={6} fill="#2AC0D5" /> */}
              <text className="item-value" transform="translate(4,4)" fill="#2AC0D5" fontSize="11">
                <tspan x="0" dy="1em">Sold to Grid: {tooltipValue3}</tspan>
              </text>
            </g>
          </g>
        </g>
      </svg>
      <div className="flex items-center mt-6 ml-5 text-sm">
        <div className="bg-blue-700 w-4 h-4 my-1 mr-2"></div>
        <div className="mr-3">Electricity Consumed (CHP vs Grid)</div>
        <div className="bg-cyan-500 w-4 h-4 my-1 mr-2"></div>
        <div>Sold To The Grid</div>
      </div>
    </div>
  );
}
